
/**
 * Strips HTML tags to produce plain text.
 * Used for networks that don't support formatting (e.g., Instagram).
 */
export const stripHtml = (html: string): string => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
};

/**
 * Prepares HTML for Telegram.
 * 1. Converts internal visual spoilers (<span class="spoiler">) to <tg-spoiler> (or keep as is if backend handles it).
 *    Note: Telegram Bot API supports <tg-spoiler>, <b>, <i>, <s>, <u>, <code>, <pre>, <a href>.
 * 2. Removes unsupported styles/classes.
 */
export const cleanHtmlForTelegram = (html: string): string => {
    if (!html) return "";
    
    // Replace custom spoiler spans with Telegram's tag (or compatible format)
    // Here we assume the backend or Telegram accepts <tg-spoiler>
    let clean = html
        .replace(/<span class="spoiler"[^>]*>(.*?)<\/span>/gi, '<tg-spoiler>$1</tg-spoiler>')
        .replace(/<span class="tg-spoiler"[^>]*>(.*?)<\/span>/gi, '<tg-spoiler>$1</tg-spoiler>');

    // Basic sanitization could happen here, but usually backend handles security.
    // For now, we ensure the "visual" editing artifacts are converted to semantic tags where possible.
    
    return clean;
};

/**
 * Inserts HTML or Text at the current cursor position in a ContentEditable element.
 */
export const insertHtmlAtCursor = (html: string) => {
    const sel = window.getSelection();
    if (sel && sel.getRangeAt && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();

        // Create a temporary element to hold the HTML
        const el = document.createElement("div");
        el.innerHTML = html;
        
        const frag = document.createDocumentFragment();
        let node, lastNode;
        while ((node = el.firstChild)) {
            lastNode = frag.appendChild(node);
        }
        
        range.insertNode(frag);

        // Move cursor after inserted content
        if (lastNode) {
            range.setStartAfter(lastNode);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }
};

/**
 * Wraps current selection in a specific tag/class (e.g. Spoiler)
 */
export const wrapSelection = (tag: string, className?: string) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    
    const range = sel.getRangeAt(0);
    if (range.collapsed) return; // Do nothing if no text selected

    const wrapper = document.createElement(tag);
    if (className) wrapper.className = className;
    
    try {
        range.surroundContents(wrapper);
    } catch (e) {
        console.warn("Could not wrap selection, possibly crossing block boundaries.");
        // Fallback: simple execCommand if applicable, or advanced range manipulation (omitted for brevity)
    }
};
